/**
 * S3 Governance tab: decision mode, hard fences, compute classes, archetype.
 *
 * Claim quality is owned by defaultDecisionMode (Provisional / Verified / Evidence only).
 * Execution stays propose+approve (HITL) — not a parallel authoring choice.
 */

import React, { useEffect, useMemo } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { GovernanceNeverFieldset } from '@/components/ai-studio/definition';
import {
  STUDIO_COMPUTE_CLASSES,
  STUDIO_DECISION_MODES,
  STUDIO_AGENT_ARCHETYPES,
  ARCHETYPE_CLASS_MAP,
} from '@/types/aiStudioAgent';
import type {
  StudioAgentArchetype,
  StudioComputeClass,
  StudioDecisionMode,
  StudioExecutionMode,
  UpdateVersionRequest,
} from '@/types/aiStudioAgent';

interface VersionGovernanceTabProps {
  /** Kept for parent API stability; always persisted as propose_approve. */
  executionMode: StudioExecutionMode;
  defaultDecisionMode: StudioDecisionMode;
  autonomyFences: Record<string, unknown>;
  computeClasses: StudioComputeClass[];
  /** Dominant compute class for this version — filtered to declared classes. */
  archetype: string | null;
  /** Kept for parent API stability (structured fences need no document remount). */
  documentKey: string;
  disabled: boolean;
  onChange: (patch: UpdateVersionRequest) => void;
  onValidityChange: (key: string, valid: boolean) => void;
}

const LABEL_CLASS = 'mb-1 block text-base font-medium text-[var(--color-surface-text)]';
const HINT_CLASS = 'mb-2 text-base text-[var(--color-surface-text-secondary)]';

export const VersionGovernanceTab: React.FC<VersionGovernanceTabProps> = ({
  executionMode: _executionMode,
  defaultDecisionMode,
  autonomyFences,
  computeClasses,
  archetype,
  documentKey: _documentKey,
  disabled,
  onChange,
  onValidityChange,
}) => {
  const { t } = useLanguage();

  useEffect(() => {
    onValidityChange('autonomyFences', true);
  }, [onValidityChange]);

  // Lock HITL propose+approve whenever this tab mounts (removes legacy autonomous picks).
  useEffect(() => {
    onChange({
      executionMode: 'propose_approve',
      hitlRequired: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot normalize on mount
  }, []);

  const availableArchetypes = useMemo(
    () => STUDIO_AGENT_ARCHETYPES.filter((a) => computeClasses.includes(ARCHETYPE_CLASS_MAP[a])),
    [computeClasses]
  );

  const toggleComputeClass = (computeClass: StudioComputeClass, enabled: boolean) => {
    const next = STUDIO_COMPUTE_CLASSES.filter((candidate) =>
      candidate === computeClass ? enabled : computeClasses.includes(candidate)
    );
    if (next.length === 0) return;

    const currentArchetype = archetype as StudioAgentArchetype | null;
    const archetypeClassRemoved =
      currentArchetype &&
      ARCHETYPE_CLASS_MAP[currentArchetype] === computeClass &&
      !enabled;

    onChange({
      computeClasses: next,
      ...(archetypeClassRemoved ? { archetype: null } : {}),
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className={LABEL_CLASS} htmlFor="version-default-decision-mode">
            {t('aiStudio.definition.defaultDecisionMode')}
          </label>
          <p className={HINT_CLASS}>{t('aiStudio.definition.defaultDecisionModeHint')}</p>
          <select
            id="version-default-decision-mode"
            className="w-full rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 py-2 text-base text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] disabled:opacity-60"
            value={defaultDecisionMode}
            disabled={disabled}
            onChange={(e) =>
              onChange({ defaultDecisionMode: e.target.value as StudioDecisionMode })
            }
          >
            {STUDIO_DECISION_MODES.map((mode) => (
              <option key={mode} value={mode}>
                {t(`aiStudio.definition.defaultDecisionMode.${mode}`)}
              </option>
            ))}
          </select>
        </div>
        <GovernanceNeverFieldset
          value={autonomyFences}
          disabled={disabled}
          onChange={(next) => onChange({ autonomyFences: next })}
        />
      </div>

      <fieldset>
        <legend className={LABEL_CLASS}>{t('aiStudio.versions.computeClasses')}</legend>
        <p className={HINT_CLASS}>{t('aiStudio.versions.computeClassesHint')}</p>
        <div className="flex flex-wrap gap-4">
          {STUDIO_COMPUTE_CLASSES.map((computeClass) => (
            <label
              key={computeClass}
              className="flex items-center gap-2 text-base text-[var(--color-surface-text)]"
            >
              <input
                type="checkbox"
                checked={computeClasses.includes(computeClass)}
                disabled={disabled}
                onChange={(event) => toggleComputeClass(computeClass, event.target.checked)}
              />
              {t(`aiStudio.versions.computeClass.${computeClass}`)}
            </label>
          ))}
        </div>
      </fieldset>

      <div>
        <label className={LABEL_CLASS} htmlFor="version-archetype">
          {t('aiStudio.versions.archetype')}
        </label>
        <p className={HINT_CLASS}>{t('aiStudio.versions.archetypeHint')}</p>
        <select
          id="version-archetype"
          className="w-full rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 py-2 text-base text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] disabled:opacity-60"
          value={archetype ?? ''}
          disabled={disabled || availableArchetypes.length === 0}
          onChange={(e) => onChange({ archetype: e.target.value || null })}
        >
          <option value="">{t('aiStudio.common.none')}</option>
          {availableArchetypes.map((a) => (
            <option key={a} value={a}>
              {t(`aiStudio.versions.archetype.${a}`)}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};
